import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { WORLD } from '../../core/config';
import { beamSolid, boxSolid } from '../fabric/solids';

const TIMBER = { navigationWidth: 6, footY: -2.5, pileRadius: 0.16, thickness: 0.3 } as const;

/** The period woodcut shows bents beneath the fixed timber portions. Their count,
 * spacing, submerged construction and the six-metre movable opening are estimates. */
export function nieuweBrugTimberPlan(length: number, width: number) {
  const navigationWidth = Math.min(TIMBER.navigationWidth, length / 3);
  const middle = length / 2;
  const opening = [middle - navigationWidth / 2, middle + navigationWidth / 2] as const;
  const fixed = [
    [0, opening[0]],
    [opening[1], length],
  ] as const;
  const bents = fixed.flatMap(([from, to]) => [
    from + (to - from) / 4,
    from + (3 * (to - from)) / 4,
  ]);
  return { opening, fixed, bents, pileAcross: width / 2 - 0.5, footY: TIMBER.footY };
}

/** Separate fixed deck, movable leaf and supported timber bents, without piles or
 * diagonal stays across the navigation opening. The walking top is unchanged. */
export function buildNieuweBrugTimber(
  builder: MeshBuilder,
  wood: VertexStyle,
  frame: Frame,
  length: number,
  width: number,
  height: number
): void {
  const plan = nieuweBrugTimberPlan(length, width);
  const half = width / 2;
  const leaf = { ...wood, color: wood.color.map((c) => c * 0.94) as [number, number, number] };
  for (const [from, to, style] of [
    [0, plan.opening[0], wood],
    [...plan.opening, leaf],
    [plan.opening[1], length, wood],
  ] as const)
    boxSolid(builder, style, frame, [from, height - TIMBER.thickness, -half], [to, height, half], {
      top: true,
      bottom: true,
    });
  const capY = height - TIMBER.thickness - 0.16;
  for (const x of plan.bents) {
    for (const z of [-plan.pileAcross, plan.pileAcross])
      builder.cylinder(
        wood,
        frame,
        [x, plan.footY, z],
        TIMBER.pileRadius,
        TIMBER.pileRadius,
        capY - plan.footY,
        8
      );
    beamSolid(builder, wood, frame, [x, capY, -half + 0.18], [x, capY, half - 0.18], 0.16);
    for (const direction of [-1, 1])
      beamSolid(
        builder,
        wood,
        frame,
        [x, WORLD.waterY - 0.15, direction * plan.pileAcross],
        [x, capY - 0.2, -direction * plan.pileAcross],
        0.09
      );
  }
  for (const [from, to] of plan.fixed) {
    const [a, b] = plan.bents.filter((x) => x > from && x < to) as [number, number];
    for (const z of [-plan.pileAcross, plan.pileAcross]) {
      beamSolid(
        builder,
        wood,
        frame,
        [from - 0.1, height - 0.45, z],
        [to + 0.1, height - 0.45, z],
        0.15
      );
      beamSolid(builder, wood, frame, [a, WORLD.waterY - 0.15, z], [b, capY - 0.2, z], 0.09);
    }
  }
  // The movable leaf's stringers are above head clearance and land on the two fixed ends.
  for (const z of [-plan.pileAcross, plan.pileAcross])
    beamSolid(
      builder,
      wood,
      frame,
      [plan.opening[0] - 0.1, height - 0.45, z],
      [plan.opening[1] + 0.1, height - 0.45, z],
      0.15
    );
}
